import { Schema } from "effect";

/**
 * Cabin configuration input from the user.
 * Represents the number of passengers in each class.
 */
export class CabinConfiguration extends Schema.Class<CabinConfiguration>(
  "CabinConfiguration"
)({
  /** Number of business class passengers (no limit - allows overbooking scenarios) */
  businessPassengers: Schema.Number.pipe(Schema.int(), Schema.nonNegative()),
  /** Number of economy class passengers (no limit - allows overbooking scenarios) */
  economyPassengers: Schema.Number.pipe(Schema.int(), Schema.nonNegative()),
  /** Number of lap infants (don't need their own seat) */
  lapInfants: Schema.Number.pipe(Schema.int(), Schema.nonNegative()),
}) {}

/**
 * Seat occupancy calculation result.
 * Contains all computed values for the cabin layout.
 */
export class SeatOccupancy extends Schema.Class<SeatOccupancy>("SeatOccupancy")(
  {
    /** Number of rows configured as business class */
    businessRows: Schema.Number.pipe(Schema.int(), Schema.nonNegative()),
    /** Number of rows configured as economy class */
    economyRows: Schema.Number.pipe(Schema.int(), Schema.nonNegative()),
    /** Total available seats in the cabin */
    totalAvailableSeats: Schema.Number.pipe(Schema.int(), Schema.nonNegative()),
    /** Number of seats currently occupied */
    occupiedSeats: Schema.Number.pipe(Schema.int(), Schema.nonNegative()),
    /** Remaining available seats (can be negative if overbooked) */
    availableSeats: Schema.Number.pipe(Schema.int()),
    /** Utilization percentage (0-100+) */
    utilizationPercent: Schema.Number.pipe(Schema.nonNegative()),
    /** Number of overbooked passengers (0 if not overbooked) */
    overbookedCount: Schema.Number.pipe(Schema.int(), Schema.nonNegative()),
    /** Whether lap infants exceed economy passengers */
    lapInfantWarning: Schema.Boolean,
  }
) {}

/**
 * Represents a single seat in the cabin.
 */
export class Seat extends Schema.Class<Seat>("Seat")({
  /** Row number (1-31, skipping 13) */
  row: Schema.Number.pipe(Schema.int(), Schema.positive()),
  /** Seat letter (A-F) */
  letter: Schema.Literal("A", "B", "C", "D", "E", "F"),
  /** Whether this seat is blocked (middle seats in business) */
  isBlocked: Schema.Boolean,
  /** Whether this seat is occupied */
  isOccupied: Schema.Boolean,
  /** Whether this seat has a lap infant */
  hasLapInfant: Schema.Boolean,
  /** Cabin class */
  cabinClass: Schema.Literal("business", "economy"),
}) {}

/**
 * Complete cabin layout with all seats.
 */
export class CabinLayout extends Schema.Class<CabinLayout>("CabinLayout")({
  /** All seats in the cabin, organized by row */
  seats: Schema.Array(Seat),
  /** The calculated seat occupancy metrics */
  occupancy: SeatOccupancy,
}) {}

/**
 * Supported languages for i18n.
 */
export const SupportedLanguage = Schema.Literal("en", "es", "sv");
export type SupportedLanguage = Schema.Schema.Type<typeof SupportedLanguage>;

/**
 * User preferences stored in localStorage.
 */
export class UserPreferences extends Schema.Class<UserPreferences>(
  "UserPreferences"
)({
  language: SupportedLanguage,
  analyticsConsent: Schema.Boolean,
}) {}

/**
 * Aircraft configuration defining cabin layout parameters.
 */
export class AircraftConfig extends Schema.Class<AircraftConfig>(
  "AircraftConfig"
)({
  /** Unique identifier for the aircraft type */
  id: Schema.String,
  /** Airline/company name */
  airline: Schema.String,
  /** Aircraft model name */
  model: Schema.String,
  /** Number of business class rows */
  businessRows: Schema.Number.pipe(Schema.int(), Schema.nonNegative()),
  /** Number of economy class rows */
  economyRows: Schema.Number.pipe(Schema.int(), Schema.nonNegative()),
  /** Seats per row in business class (0 if no business class) */
  businessSeatsPerRow: Schema.Number.pipe(Schema.int(), Schema.nonNegative()),
  /** Seats per row in economy class */
  economySeatsPerRow: Schema.Number.pipe(Schema.int(), Schema.positive()),
  /** Whether row 13 is skipped */
  skipRow13: Schema.Boolean,
}) {}

/**
 * Predefined aircraft configurations.
 * Currently only SAS A320 Neo is supported.
 */
export const AIRCRAFT_CONFIGS: readonly AircraftConfig[] = [
  new AircraftConfig({
    id: "sas-a320neo",
    airline: "SAS",
    model: "Airbus A320 Neo",
    businessRows: 12,
    economyRows: 14,
    businessSeatsPerRow: 4,
    economySeatsPerRow: 6,
    skipRow13: true,
  }),
] as const;
